import * as React from "react"
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { Grid, Typography } from "@material-ui/core"
import { IGatsbyImageData } from "gatsby-plugin-image"
import { Button } from "gatsby-theme-material-ui"
import { GatsbyImage } from 'gatsby-plugin-image'
import Badge from "../Badge";
import { Link } from "gatsby";
import ReactMarkdown from "react-markdown"
import { TEXT_BODY, TEXT_HEADING } from "../../styles/homepageSections";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            padding: '50px 0',
            [theme.breakpoints.down('sm')]: {
                padding: '32px 0',
            },
        },
        subheadline: {
            color: theme.palette.primary.main,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            marginBottom: 12,
            display: 'block',
            [theme.breakpoints.up('md')]: {
                fontSize: 13,
                marginBottom: 16,
            },
        },
        title: {
            fontWeight: 600,
            color: TEXT_HEADING,
        },
        description: {
            padding: '10px 0 24px 0',
            color: TEXT_BODY,
            fontSize: 16,
            lineHeight: 1.55,
            [theme.breakpoints.up('md')]: {
                fontSize: 18,
                lineHeight: 1.6,
            },
        },
        link: {
            textDecoration: 'none',
            display: 'inline-block',
        },
        pc: {
            [theme.breakpoints.down('sm')]: {
                display: 'none',
            },
        },
        mobile: {
            [theme.breakpoints.up('md')]: {
                display: 'none',
            },
        },
        markdown: {
            '& a': {
                color: '#ea1674',
                fontWeight: 'bold',
                textDecoration: 'underline',
            },
            '& p': {
                margin: 0,
            },
        },
        imageWrapper: {
            height: '250px',
            overflow: 'hidden',
            borderRadius: 8,
            [theme.breakpoints.up('md')]: {
                height: '400px',
            },
        },
        image: {
            maxWidth: '100%',
        },
    }),
);

interface Props {
    url: string;
    image?: IGatsbyImageData;
    title: string;
    subheadline?: string | null;
    description: string;
    imageRight?: boolean;
    buttonPrimary?: boolean;
    buttonText: string;
    badge?: string;
    anchor?: string;
}

const hasText = (value?: string | null) =>
    Boolean(value && value.trim());

const RowContent: React.FC<{
    classes: ReturnType<typeof useStyles>;
    title: string;
    subheadline?: string | null;
    description: string;
    buttonText: string;
    url: string;
    buttonPrimary?: boolean;
    badge?: string;
}> = ({ classes, title, subheadline, description, buttonText, url, buttonPrimary, badge }) => (
    <>
        {hasText(subheadline) && (
            <Typography component="span" className={classes.subheadline}>
                {subheadline}
            </Typography>
        )}
        {badge && <Badge text={badge} />}
        <Typography variant="h3" gutterBottom className={classes.title}>
            {title}
        </Typography>
        <Typography component="div" variant="body1" className={classes.description}>
            <ReactMarkdown linkTarget="_blank" className={classes.markdown}>{description}</ReactMarkdown>
        </Typography>
        {buttonText && (
            <Link to={url} className={classes.link}>
                <Button
                    variant={buttonPrimary ? "contained" : "outlined"}
                    color="primary"
                    size="large"
                >
                    {buttonText}
                </Button>
            </Link>
        )}
    </>
);

const Row: React.FC<Props> = ({ imageRight, url, image, title, subheadline, description, buttonPrimary, buttonText, badge, anchor }) => {
    const classes = useStyles();

    const contentProps = {
        classes,
        title,
        subheadline,
        description,
        buttonText,
        url,
        buttonPrimary,
        badge,
    };

    return (
        <div id={anchor}>
            <Grid container spacing={3} alignItems="center" className={[classes.root, classes.pc].join(" ")}>
                {imageRight ? <React.Fragment>
                    <Grid item md={5}>
                        <RowContent {...contentProps} />
                    </Grid>
                    <Grid item md={1} />
                    <Grid item md={6} className={classes.imageWrapper}>
                        {image && <GatsbyImage image={image} alt={title} className={classes.image} />}
                    </Grid>
                </React.Fragment>
                    : <React.Fragment>
                        <Grid item md={6} className={classes.imageWrapper}>
                            {image && <GatsbyImage image={image} alt={title} className={classes.image} />}
                        </Grid>
                        <Grid item md={1} />
                        <Grid item md={5}>
                            <RowContent {...contentProps} />
                        </Grid>
                    </React.Fragment>}
            </Grid>
            <Grid container spacing={3} className={[classes.root, classes.mobile].join(" ")}>
                <Grid item xs={12} className={classes.imageWrapper}>
                    {image && <GatsbyImage image={image} alt={title} className={classes.image} />}
                </Grid>
                <Grid item xs={12}>
                    <RowContent {...contentProps} />
                </Grid>
            </Grid>
        </div>
    )
}

export default Row
